/**
 * /admin/scheduled-jobs — catalog list view (#1278 / #1280).
 *
 * One row per registered scheduled job. The table reads straight from
 * the `ScheduledJob` collection via the admin API; every visible row
 * corresponds to one Mongo document. No "Add" affordance — rows
 * appear automatically via the boot seeder when a new job is
 * registered in code.
 *
 * Columns:
 *   - Job: name + description
 *   - Schedule: human-time version of the cron + the raw expression
 *   - Status: pill (Active / Paused / Disabled / Last-run-failed / Off /
 *     Code-owned). Off is a job that does not apply to this install, such
 *     as a price list for a supplier with no key; the sentence under the
 *     pill names what it waits for, and it has no Run now.
 *   - Last 30 runs: bar strip (placeholder dot; the run-history strip
 *     is a future enhancement — for now we show the last-run status
 *     pill alone)
 *   - Next run: when + relative
 *   - Per-row menu: Configure / Run now / Pause/Resume / View detail /
 *     Delete (only when `registered === false`)
 *
 * Timestamps render through `useUserTimezone()` — switch the
 * operator's timezone preference and the column re-renders in the
 * new zone on the next paint.
 *
 * **`registered: false` banner.** A row whose code definition has
 * been removed surfaces a yellow banner; Delete is the only mutation
 * available.
 *
 * **`editPolicy === 'code-owned-schedule'` rows** carry a "Code-
 * owned" pill in the Status column so operators see at a glance
 * which rows they can't retime.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, Pause, Play, Power, RotateCw, Settings } from 'lucide-react'
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { useToast } from '@/hooks/useToast'
import { formatLocalDateTime, formatRelativeTime } from '@/lib/utils'
import {
  adminScheduledJobsAPI,
  type ScheduledJobRow,
} from '@/lib/api'
import { ScheduledJobConfigureDialog } from './ConfigureDialog'

export function ScheduledJobsPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const tz = useUserTimezone()
  const [rows, setRows] = useState<ScheduledJobRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [configureJobId, setConfigureJobId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    try {
      setLoading(true)
      const { jobs } = await adminScheduledJobsAPI.list()
      setRows(jobs)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load scheduled jobs.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  async function handleRunNow(jobId: string, displayName: string) {
    try {
      const { runId } = await adminScheduledJobsAPI.runNow(jobId)
      toast({
        variant: 'success',
        title: 'Run started',
        description: (
          <span>
            Started a run of <strong>{displayName}</strong>. Run id:{' '}
            <code className="text-[11px]">{runId}</code>.{' '}
            <Link
              to={`/admin/audit?action=scheduledJob.run_now&targetId=${encodeURIComponent(jobId)}`}
              className="underline"
            >
              View in audit log
            </Link>
          </span>
        ),
      })
      void reload()
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not start a run',
        description: err instanceof Error ? err.message : 'Unknown error.',
      })
    }
  }

  async function handleStatusChange(
    job: ScheduledJobRow,
    nextStatus: 'active' | 'paused' | 'disabled',
  ) {
    try {
      const updated = await adminScheduledJobsAPI.patch(job.jobId, { status: nextStatus })
      setRows((prev) => prev?.map((r) => (r.jobId === updated.jobId ? updated : r)) ?? null)
      const verb =
        nextStatus === 'active'
          ? 'Resumed'
          : nextStatus === 'paused'
            ? 'Paused'
            : 'Disabled'
      const auditAction =
        nextStatus === 'active'
          ? 'scheduledJob.resumed'
          : nextStatus === 'paused'
            ? 'scheduledJob.paused'
            : 'scheduledJob.disabled'
      toast({
        variant: 'success',
        title: `${verb} ${updated.displayName}`,
        description: (
          <Link
            to={`/admin/audit?action=${auditAction}&targetId=${encodeURIComponent(job.jobId)}`}
            className="underline"
          >
            View in audit log
          </Link>
        ),
      })
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not change status',
        description: err instanceof Error ? err.message : 'Unknown error.',
      })
    }
  }

  async function handleDelete(job: ScheduledJobRow) {
    if (!window.confirm(`Delete the catalog entry for "${job.displayName}"? This cannot be undone.`)) {
      return
    }
    try {
      await adminScheduledJobsAPI.delete(job.jobId)
      setRows((prev) => prev?.filter((r) => r.jobId !== job.jobId) ?? null)
      toast({
        variant: 'success',
        title: `Removed ${job.displayName}`,
        description: (
          <Link
            to={`/admin/audit?action=scheduledJob.deleted&targetId=${encodeURIComponent(job.jobId)}`}
            className="underline"
          >
            View in audit log
          </Link>
        ),
      })
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not delete',
        description: err instanceof Error ? err.message : 'Unknown error.',
      })
    }
  }

  return (
    <div className="space-y-6 max-w-full">
      <div className="text-xs text-muted-foreground">
        <Link to="/admin" className="hover:text-foreground">
          Global Admin
        </Link>
        {' · '}
        <span>Scheduled Jobs</span>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">Scheduled Jobs</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Recurring background jobs registered in code. Jobs appear here automatically when added in
          a deploy; rows you see here are tunable (cron, time zone, alerts, pause / resume).{' '}
          <span className="text-muted-foreground/80">All times shown in your time zone ({tz}).</span>
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !rows ? (
        <p className="text-sm text-muted-foreground">Loading scheduled jobs…</p>
      ) : rows && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No scheduled jobs have been registered yet.</p>
      ) : rows ? (
        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Schedule</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last run</TableHead>
                <TableHead>Next run</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <CatalogRow
                  key={row.jobId}
                  row={row}
                  tz={tz}
                  onConfigure={() => setConfigureJobId(row.jobId)}
                  onRunNow={() => handleRunNow(row.jobId, row.displayName)}
                  onStatusChange={(next) => handleStatusChange(row, next)}
                  onViewDetail={() => navigate(`/admin/scheduled-jobs/${row.jobId}`)}
                  onDelete={() => handleDelete(row)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {configureJobId && (
        <ScheduledJobConfigureDialog
          jobId={configureJobId}
          open
          onClose={() => setConfigureJobId(null)}
          onSaved={(updated) => {
            setRows((prev) =>
              prev?.map((r) => (r.jobId === updated.jobId ? updated : r)) ?? null,
            )
            setConfigureJobId(null)
            toast({
              variant: 'success',
              title: `Saved changes to ${updated.displayName}`,
              description: (
                <Link
                  to={`/admin/audit?action=scheduledJob.config_updated&targetId=${encodeURIComponent(updated.jobId)}`}
                  className="underline"
                >
                  View in audit log
                </Link>
              ),
            })
          }}
        />
      )}
    </div>
  )
}

interface CatalogRowProps {
  row: ScheduledJobRow
  tz: string
  onConfigure: () => void
  onRunNow: () => void
  onStatusChange: (next: 'active' | 'paused' | 'disabled') => void
  onViewDetail: () => void
  onDelete: () => void
}

function CatalogRow({
  row,
  tz,
  onConfigure,
  onRunNow,
  onStatusChange,
  onViewDetail,
  onDelete,
}: CatalogRowProps) {
  const isUnregistered = !row.registered
  const isCodeOwned = row.editPolicy === 'code-owned-schedule'
  return (
    <>
      {isUnregistered && (
        <TableRow>
          <TableCell colSpan={6} className="bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 text-xs">
            <strong>{row.displayName}</strong> is no longer registered in code. The runner will not
            execute it. You can remove the catalog entry below.
          </TableCell>
        </TableRow>
      )}
      <TableRow className={isUnregistered ? 'bg-amber-50/40 dark:bg-amber-950/15' : undefined}>
        <TableCell>
          <button type="button" onClick={onViewDetail} className="text-left hover:underline">
            <div className="font-medium">{row.displayName}</div>
            <div className="text-xs text-muted-foreground">{row.description}</div>
          </button>
        </TableCell>
        <TableCell>
          <div className="text-sm">{describeCron(row.cron)}</div>
          <code className="text-[11px] text-muted-foreground">{row.cron}</code>
          <div className="text-[11px] text-muted-foreground">in {row.timezone}</div>
        </TableCell>
        <TableCell>
          <StatusPill
            status={row.status}
            lastRunStatus={row.lastRunStatus}
            runInProgress={row.runInProgress}
            off={Boolean(row.off)}
          />
          {isCodeOwned && (
            <Badge variant="secondary" className="ml-1 text-[10px]">
              Code-owned
            </Badge>
          )}
          {row.off && <div className="mt-1 max-w-[32ch] text-xs text-foreground/80">{row.off.reason}</div>}
        </TableCell>
        <TableCell>
          {row.lastRunAt ? (
            <div className="text-xs">
              <div>{formatLocalDateTime(row.lastRunAt, tz)}</div>
              <div className="text-muted-foreground">{formatRelativeTime(new Date(row.lastRunAt))}</div>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">never</span>
          )}
        </TableCell>
        <TableCell>
          {row.status === 'disabled' ? (
            <span className="text-xs text-muted-foreground">disabled</span>
          ) : row.status === 'paused' ? (
            <span className="text-xs text-muted-foreground">paused</span>
          ) : row.off ? (
            <span className="text-xs text-muted-foreground">off</span>
          ) : row.nextRunAt ? (
            <div className="text-xs">
              <div>{formatLocalDateTime(row.nextRunAt, tz)}</div>
              <div className="text-muted-foreground">{formatRelativeTime(new Date(row.nextRunAt))}</div>
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell className="text-right">
          <RowMenu
            row={row}
            onConfigure={onConfigure}
            onRunNow={onRunNow}
            onStatusChange={onStatusChange}
            onViewDetail={onViewDetail}
            onDelete={onDelete}
          />
        </TableCell>
      </TableRow>
    </>
  )
}

function RowMenu({
  row,
  onConfigure,
  onRunNow,
  onStatusChange,
  onViewDetail,
  onDelete,
}: Omit<CatalogRowProps, 'tz'>) {
  if (!row.registered) {
    return (
      <Button size="sm" variant="destructive" onClick={onDelete}>
        Delete
      </Button>
    )
  }
  // Inline icon actions — same `Button variant="ghost" size="icon-sm"`
  // pattern PlansCatalog and other admin tables use across the app. The
  // `title` attr drives the hover tooltip; `aria-label` carries the same
  // wording for screen readers (and is the accessible name the tests
  // query). Run now / Pause / Resume / Re-enable each get a distinct
  // glyph so no two visible at once look alike.
  return (
    <div className="flex justify-end gap-0.5">
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={onViewDetail}
        title="View details"
        aria-label="View details"
      >
        <Eye className="h-3.5 w-3.5" />
      </Button>
      <Button
        size="icon-sm"
        variant="ghost"
        onClick={onConfigure}
        title="Configure"
        aria-label="Configure"
      >
        <Settings className="h-3.5 w-3.5" />
      </Button>
      {/* No Run now on a job that does not apply here: the server would
          refuse it, and the sentence in the Status column says why. */}
      {!row.off && (
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={onRunNow}
          disabled={row.runInProgress}
          title="Run now"
          aria-label="Run now"
        >
          <Play className="h-3.5 w-3.5" />
        </Button>
      )}
      {row.status === 'active' && (
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={() => onStatusChange('paused')}
          title="Pause"
          aria-label="Pause"
        >
          <Pause className="h-3.5 w-3.5" />
        </Button>
      )}
      {row.status === 'paused' && (
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={() => onStatusChange('active')}
          title="Resume"
          aria-label="Resume"
        >
          <RotateCw className="h-3.5 w-3.5" />
        </Button>
      )}
      {row.status === 'disabled' && (
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={() => onStatusChange('active')}
          title="Re-enable"
          aria-label="Re-enable"
        >
          <Power className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}

function StatusPill({
  status,
  lastRunStatus,
  runInProgress,
  off,
}: {
  status: ScheduledJobRow['status']
  lastRunStatus: ScheduledJobRow['lastRunStatus']
  runInProgress: boolean
  off: boolean
}) {
  if (runInProgress) {
    return <Badge variant="default">Running</Badge>
  }
  // An operator's choice stays visible: Off replaces only what an active job
  // would show, and the sentence beside the pill says why either way.
  if (status === 'disabled') {
    return <Badge variant="secondary">Disabled</Badge>
  }
  if (status === 'paused') {
    return <Badge variant="secondary">Paused</Badge>
  }
  // status === 'active'
  if (off) {
    return <Badge variant="secondary">Off</Badge>
  }
  if (lastRunStatus === 'failed' || lastRunStatus === 'timed-out') {
    return <Badge variant="destructive">Last run {lastRunStatus === 'failed' ? 'failed' : 'timed out'}</Badge>
  }
  return <Badge variant="default">Active</Badge>
}

/** Translate a cron expression into a plain-English summary for the
 * common cadence patterns. Falls back to a generic "Custom schedule"
 * label for any expression outside the picker's set — the raw cron
 * text shows beneath this on the row, so the operator still sees
 * the literal value. Plain-language sweep: no "cron expression",
 * "interval", or "tick" jargon. */
function describeCron(cron: string): string {
  switch (cron.trim()) {
    case '0 * * * *':
      return 'Every hour'
    case '0 */6 * * *':
      return 'Every 6 hours'
    case '0 2 * * *':
      return 'Daily at 02:00'
    case '0 3 * * *':
      return 'Daily at 03:00'
    case '0 22 * * *':
      return 'Daily at 22:00'
    case '0 2 * * 0':
      return 'Weekly · Sundays 02:00'
    default:
      return 'Custom schedule'
  }
}
